import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import imageCompression from 'browser-image-compression';
import { useDropzone, DropzoneState } from 'react-dropzone';
import { UPDATE_CAR, GET_PRESIGNED_URLS } from '../../../services/carService/queries';
import { CAR_LISTING_STATUS } from '../../../services/listingHoldService/queries';
import { ICarListingStatusQuery } from '../../../services/listingHoldService/__generated__/queries.generated';
import { ListingHoldReason } from '../../../../__generated__/graphql';
import { addCarRecallNotice } from '../../../utils/listingHold';
import { getBrand } from '../../../constants/brands';
import { useEditingCarDraft } from '../../../hooks/useEditingCarDraft';
import { Colors } from '../../../styles/colors';
import { useSnapshot } from 'valtio';
import { carActions, carStore } from '../../../../store/carStore';
import { toast } from 'react-toastify';
import { evictMyCarsV2Cache } from '../../../utils/evictMyCarsV2Cache';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

// ------------------------------------------
// 样式定义 (纯 styled-components)
// ------------------------------------------

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 2rem;
`;

const Description = styled.p`
  color: ${Colors.Gray600};
  margin-bottom: 1.5rem;
`;

const PhotosGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1.5rem;
`;

const PhotoPreview = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 0.5rem;
  overflow: hidden;
  width: calc(50% - 0.5rem);

  @media (min-width: 768px) {
    width: calc(33.333% - 0.667rem);
  }

  @media (min-width: 1024px) {
    width: calc(25% - 0.75rem);
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MainBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${Colors.PrimaryRed};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  z-index: 1;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${Colors.PrimaryRed};
  color: ${Colors.White};
  border-radius: 9999px;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition-property: background-color;
  transition-duration: 150ms;
  &:hover {
    background-color: ${Colors.PrimaryRedDark};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  width: 100%;
  color: white;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  border-radius: 0.25rem;
  transition-property: background-color;
  transition-duration: 150ms;
  ${({ isActivated }) =>
    isActivated
      ? css`
          background-color: ${Colors.PrimaryRed};
          &:hover {
            background-color: ${Colors.PrimaryRedDark};
          }
        `
      : css`
          background-color: ${Colors.Gray400};
          cursor: not-allowed;
        `}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 1.5rem;
`;

const ErrorMessage = styled.p`
  color: ${Colors.Error};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const DropzoneArea = styled.div<Partial<DropzoneState>>`
  aspect-ratio: 1 / 1;
  border: 2px dashed ${Colors.Gray300};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${Colors.Gray50};
  width: calc(50% - 0.5rem);

  @media (min-width: 768px) {
    width: calc(33.333% - 0.667rem);
  }

  @media (min-width: 1024px) {
    width: calc(25% - 0.75rem);
  }

  &:hover {
    border-color: ${Colors.PrimaryRed};
    background-color: ${Colors.Red50};
    color: ${Colors.PrimaryRed};
  }

  ${({ isDragActive }) =>
    isDragActive &&
    css`
      border-color: ${Colors.PrimaryRed};
      background-color: ${Colors.Red50};
    `}
`;

const PlusIcon = styled.div`
  font-size: 3rem;
  font-weight: 300;
  color: ${Colors.Gray400};
  line-height: 1;
  margin-bottom: 0.5rem;

  ${DropzoneArea}:hover & {
    color: ${Colors.PrimaryRed};
  }
`;

const AddText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${Colors.Gray500};

  ${DropzoneArea}:hover & {
    color: ${Colors.PrimaryRed};
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: ${Colors.Gray200};
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    height: 100%;
    width: ${({ progress }) => `${progress}%`};
    background-color: ${Colors.PrimaryRed};
    transition: width 0.3s ease;
  }
`;

const StrictModeDroppable = ({ children, ...props }: any) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const DroppableComponent = Droppable as any;
  return <DroppableComponent {...props}>{children}</DroppableComponent>;
};

// ------------------------------------------
// 组件逻辑
// ------------------------------------------

export default function AddPhotosPage() {
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: number]: number;
  }>({});

  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { editingCar, ready: editingCarReady } = useEditingCarDraft();
  const carStoreSnapshot = useSnapshot(carStore);
  // Use carStore as fallback if editingCar from hook is not available
  const currentEditingCar = editingCar || carStoreSnapshot.editingCar;

  const [updateCar] = useMutation(UPDATE_CAR);
  const [getPresignedUrls] = useMutation(GET_PRESIGNED_URLS);

  // compressImage 保持不变
  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 4,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return new File([compressedFile], file.name, {
        type: compressedFile.type,
      });
    } catch (error) {
      return file;
    }
  };

  // 💡 关键修改：onDrop 逻辑重新加入异步压缩
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const fileArray = acceptedFiles;

      const imageFiles = fileArray.filter(file => {
        const isValidType = file.type.startsWith('image/');
        const isValidSize = file.size <= 10 * 1024 * 1024;

        if (!isValidType) {
          setError('Please select only image files');
          return false;
        }
        if (!isValidSize) {
          setError('File size must be less than 10MB');
          return false;
        }
        return true;
      });

      const remainingSlots = 10 - photos.length;
      if (imageFiles.length > remainingSlots) {
        setError(
          `You can only choose ${remainingSlots} more ${remainingSlots === 1 ? 'photo' : 'photos'} to upload`,
        );
        return;
      }

      setError('');

      // 🌟 重新加入异步压缩步骤
      const processedFiles = await Promise.all(imageFiles.map(file => compressImage(file)));

      const newPreviews = processedFiles.map(file => URL.createObjectURL(file));

      setPhotos(prev => [...prev, ...processedFiles]);
      setPreviews(prev => [...prev, ...newPreviews]);
    },
    [photos.length],
  );

  // useDropzone hook 配置保持不变
  const { getRootProps, getInputProps, isDragActive, fileRejections, open } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    maxFiles: 10,
    multiple: true,
    disabled: photos.length >= 10 || isUploading,
    noClick: true,
  });

  // 监听文件拒绝，用于显示错误信息
  useEffect(() => {
    if (fileRejections.length > 0) {
      const rejectedFile = fileRejections[0];
      const errorMsg = rejectedFile.errors
        .map(e => {
          if (e.code === 'file-too-large') return 'File size must be less than 10MB';
          if (e.code === 'file-invalid-type') return 'Please select only image files';
          if (e.code === 'too-many-files') return 'Maximum 10 photos allowed';
          return 'File upload failed or was rejected.';
        })
        .join(', ');
      setError(errorMsg);
    }
  }, [fileRejections]);

  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [previews]);

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newPhotos = Array.from(photos);
    const [reorderedPhoto] = newPhotos.splice(result.source.index, 1);
    newPhotos.splice(result.destination.index, 0, reorderedPhoto);

    const newPreviews = Array.from(previews);
    const [reorderedPreview] = newPreviews.splice(result.source.index, 1);
    newPreviews.splice(result.destination.index, 0, reorderedPreview);

    setPhotos(newPhotos);
    setPreviews(newPreviews);
  };

  const uploadPhotos = async () => {
    try {
      setIsUploading(true);
      setError('');

      // Use currentEditingCar which has carStore fallback
      if (!currentEditingCar?.id) {
        toast.error('Car not found. Please go back and start the listing process again.');
        navigate('/cars/host/add');
        return;
      }

      // Store the car ID to ensure we have it throughout the process
      const carId = currentEditingCar.id;

      const uploadFileToS3 = (
        file: File,
        index: number,
        s3Url: string,
        s3Key: string,
      ): Promise<string> => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.open('PUT', s3Url);
          xhr.setRequestHeader('Content-Type', 'image/jpeg');

          xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded * 100) / event.total);
              setUploadProgress(prev => ({ ...prev, [index]: percentComplete }));
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(s3Key);
            } else {
              reject(new Error(`Failed to upload photo ${index + 1}. Status: ${xhr.status}`));
            }
          };

          xhr.onerror = () => {
            reject(new Error(`Network error while uploading photo ${index + 1}.`));
          };

          xhr.send(file);
        });
      };

      const { data } = await getPresignedUrls({
        variables: {
          fileType: 'image/jpeg',
          carId: carId,
          count: photos.length,
        },
      });

      const presignedData = data.getPresignedUrls;

      const uploadedKeys = await Promise.all(
        photos.map(async (photo, index) => {
          try {
            // 上传时调用 compressImage 确保上传的是压缩后的文件
            const compressedFile = await compressImage(photo);
            const { url, key } = presignedData[index];

            return uploadFileToS3(compressedFile, index, url, key);
          } catch (error) {
            setUploadProgress(prev => ({ ...prev, [index]: 0 }));
            throw new Error(`Failed to upload photo ${index + 1}`);
          }
        }),
      );

      const imageInputs = uploadedKeys.map(url => ({
        imageUrl: url,
      }));

      // Update car with images and mark as created after all photos are uploaded
      const result = await updateCar({
        variables: {
          id: carId,
          updateCarInput: {
            images: imageInputs,
            thumbnailUrl: uploadedKeys[0] || null,
            isCreated: true, // Mark car as successfully created after photos are uploaded
          },
        },
      });

      if (result.data?.updateCar?.isCreated) {
        carActions.stopEditing();
        evictMyCarsV2Cache(apolloClient);
        // Spec 015 US-4: the server checks the model year's recalls when the car is created.
        const hold = await apolloClient
          .query<ICarListingStatusQuery>({
            query: CAR_LISTING_STATUS,
            variables: { carId },
            fetchPolicy: 'network-only',
          })
          .then(({ data }) => data.carListingStatus)
          .catch(() => null);
        if (hold?.hold?.reason === ListingHoldReason.OpenRecall) {
          toast.warning(
            `Car created. ${addCarRecallNotice({
              brandName: getBrand().name,
              supportEmail: hold.supportEmail,
              pendingManualCheck: hold.hold.pendingManualCheck,
            })}`,
            { autoClose: 15000 },
          );
          navigate(`/cars/host/view/${carId}`);
          return;
        }
        toast.success('Car created successfully!');
      }

      navigate('/cars/host');
    } catch (error: any) {
      console.error('Photo upload error:', error);
      setError(
        `Failed to upload photos: ${error.message || 'Please check network and try again.'}`,
      );
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  if (!editingCarReady) {
    return (
      <Container>
        <Title>Loading...</Title>
      </Container>
    );
  }

  // If no editing car found, show error message
  if (!currentEditingCar?.id) {
    return (
      <Container>
        <Title>Car not found</Title>
        <Description>Please go back and start the listing process again.</Description>
        <ButtonContainer>
          <NextButton isActivated onClick={() => navigate('/cars/host/add')}>
            Go Back
          </NextButton>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Add Photos</Title>
      <Description>
        Upload up to 10 high-quality photos of your car. The first photo will be your main photo.
      </Description>

      {React.createElement(
        DragDropContext as any,
        { onDragEnd: handleDragEnd } as any,
        <StrictModeDroppable droppableId="photos" direction="horizontal">
          {(provided: DroppableProvided) => (
            <PhotosGrid {...provided.droppableProps} ref={provided.innerRef as any}>
              {previews.map((preview, index) => {
                const DraggableComponent = Draggable as any;
                return (
                  <DraggableComponent key={preview} draggableId={preview} index={index}>
                    {(providedDraggable: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <PhotoPreview
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        {...providedDraggable.dragHandleProps}
                        style={{
                          ...providedDraggable.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        {index === 0 && <MainBadge>Main Photo</MainBadge>}
                        <PreviewImage src={preview} alt={`Car photo ${index + 1}`} />
                        {!isUploading && (
                          <DeleteButton onClick={() => removePhoto(index)}>×</DeleteButton>
                        )}
                        {isUploading && <ProgressBar progress={uploadProgress[index] || 0} />}
                      </PhotoPreview>
                    )}
                  </DraggableComponent>
                );
              })}
              {photos.length < 10 && (
                <DropzoneArea
                  {...getRootProps({ isDragActive })}
                  isDragActive={isDragActive}
                  onClick={open}
                  data-testid="car-photos-dropzone"
                >
                  <HiddenInput {...getInputProps()} data-testid="car-photos-input" />
                  <PlusIcon>+</PlusIcon>
                  <AddText>Add Photo</AddText>
                </DropzoneArea>
              )}
              {provided.placeholder as any}
            </PhotosGrid>
          )}
        </StrictModeDroppable>,
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonContainer>
        <NextButton
          onClick={uploadPhotos}
          isActivated={photos.length > 0 && !isUploading}
          disabled={photos.length === 0 || isUploading}
          data-testid="car-photos-next"
        >
          {isUploading ? `Uploading ${photos.length} Photos...` : 'Next'}
        </NextButton>
      </ButtonContainer>
    </Container>
  );
}
